import React, { useState, useEffect, useCallback } from 'react';
import objectsService, { TrackedObject } from '../services/ObjectsService';
import ObjectForm from './ObjectForm';
import ObjectList from './ObjectList';
import DeleteConfirmation from './DeleteConfirmation';
import ReAuthModal from './ReAuthModal';
import '../styles/ObjectManagement.css';

interface ObjectManagementState {
  objects: TrackedObject[];
  loading: boolean;
  error: string | null;
  formMode: 'closed' | 'create' | 'edit';
  editingObject: TrackedObject | null;
  deletingObject: TrackedObject | null;
  reAuthObject: TrackedObject | null;
}

const ObjectManagement: React.FC = () => {
  const [state, setState] = useState<ObjectManagementState>({
    objects: [],
    loading: true,
    error: null,
    formMode: 'closed',
    editingObject: null,
    deletingObject: null,
    reAuthObject: null,
  });
  const [apiError, setApiError] = useState<string | null>(null);
  const [refreshingIds, setRefreshingIds] = useState<Set<string>>(new Set<string>());

  const fetchObjects = useCallback(async () => {
    setState(prev => ({ ...prev, loading: true, error: null }));
    try {
      const objects = await objectsService.getAllObjects();
      setState(prev => ({ ...prev, objects, loading: false }));
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        loading: false,
        error: err.message || 'Failed to fetch objects',
      }));
    }
  }, []);

  useEffect(() => {
    fetchObjects();
  }, [fetchObjects]);

  const handleAddClick = () => {
    setApiError(null);
    setState(prev => ({ ...prev, formMode: 'create', editingObject: null }));
  };

  const handleEdit = (obj: TrackedObject) => {
    setApiError(null);
    setState(prev => ({ ...prev, formMode: 'edit', editingObject: obj }));
  };

  const handleDelete = (obj: TrackedObject) => {
    setState(prev => ({ ...prev, deletingObject: obj }));
  };

  const handleFormCancel = () => {
    setApiError(null);
    setState(prev => ({ ...prev, formMode: 'closed', editingObject: null }));
  };

  const handleFormSubmit = async (data: Partial<TrackedObject>) => {
    try {
      if (state.formMode === 'create') {
        await objectsService.createObject(data);
      } else if (state.formMode === 'edit' && state.editingObject) {
        await objectsService.updateObject(state.editingObject.id, data);
      }
      setApiError(null);
      setState(prev => ({ ...prev, formMode: 'closed', editingObject: null }));
      await fetchObjects();
    } catch (err: any) {
      setApiError(err.message || 'An error occurred while saving the object');
    }
  };

  const handleDeleteConfirm = async () => {
    if (!state.deletingObject) return;
    try {
      await objectsService.deleteObject(state.deletingObject.id);
      setState(prev => ({ ...prev, deletingObject: null }));
      await fetchObjects();
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        deletingObject: null,
        error: err.message || 'Failed to delete object',
      }));
    }
  };

  const handleDeleteCancel = () => {
    setState(prev => ({ ...prev, deletingObject: null }));
  };

  const handleReAuth = (obj: TrackedObject) => {
    setState(prev => ({ ...prev, reAuthObject: obj }));
  };

  const handleReAuthClose = () => {
    setState(prev => ({ ...prev, reAuthObject: null }));
  };

  const handleReAuthSuccess = () => {
    setState(prev => ({ ...prev, reAuthObject: null }));
    fetchObjects();
  };

  const handleRefresh = async (obj: TrackedObject) => {
    setRefreshingIds(prev => new Set(prev).add(obj.id));
    try {
      await objectsService.refreshObjectLocation(obj.id);
      await objectsService.getLatestLocation(obj.id);
    } catch (err: any) {
      setState(prev => ({
        ...prev,
        error: err.message || 'Failed to refresh location',
      }));
    } finally {
      setRefreshingIds(prev => {
        const next = new Set(prev);
        next.delete(obj.id);
        return next;
      });
    }
  };

  return (
    <div className="object-management">
      <h2>Object Management</h2>

      {state.loading && (
        <div className="loading-spinner">Loading objects...</div>
      )}

      {state.error && (
        <div className="message error">
          {state.error}
          <button className="btn-primary" onClick={fetchObjects} style={{ marginLeft: '10px' }}>
            Retry
          </button>
        </div>
      )}

      {!state.loading && !state.error && state.formMode === 'closed' && (
        <>
          <button className="btn-primary" onClick={handleAddClick}>
            Add Object
          </button>
          <ObjectList
            objects={state.objects}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onReAuth={handleReAuth}
            onRefresh={handleRefresh}
            refreshingIds={refreshingIds}
          />
        </>
      )}

      {state.formMode !== 'closed' && (
        <ObjectForm
          mode={state.formMode}
          initialData={state.editingObject ?? undefined}
          onSubmit={handleFormSubmit}
          onCancel={handleFormCancel}
          apiError={apiError}
        />
      )}

      {state.deletingObject && (
        <DeleteConfirmation
          objectName={state.deletingObject.name}
          onConfirm={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
        />
      )}

      {state.reAuthObject && (
        <ReAuthModal
          object={state.reAuthObject}
          onClose={handleReAuthClose}
          onSuccess={handleReAuthSuccess}
        />
      )}
    </div>
  );
};

export default ObjectManagement;
